import {Disposable, Environment} from 'relay-runtime';
import {commitMutation, graphql} from 'react-relay';
import Util from '../../forecast-app/shared/util/util';
import {
	DiscardNovaConversationInput,
	DiscardNovaConversationMutation,
} from './__generated__/DiscardNovaConversationMutation.graphql';
import {MutationInput, RelayOnCompleted, RelayOnError} from '../MutationTypes';

// Retires the conversation a run belongs to. The run records stay: discarding is the customer
// saying they do not want the draft, not a request to erase what was run or what it cost.
//
// Only `discarded` is really read. The thread the task modal renders comes from task.novaThread,
// which re-reads the surviving conversation for itself, so there is nothing on this payload a
// caller needs beyond confirmation that the discard landed - and `discarded` is what says so,
// rather than an outcome string that reads the same on a record discarded an hour ago. `id` is
// selected because Relay needs it to place the record in its store.
const mutation = graphql`
	mutation DiscardNovaConversationMutation($input: DiscardNovaConversationInput!) {
		discardNovaConversation(input: $input) {
			run {
				id
				discarded
			}
			# The task goes back to having no conversation, and every row showing it must say so at
			# once rather than at the next page load. Relay merges this into the task node those rows
			# already read through, which is what returns their control to offering a fresh run.
			task {
				id
				hasNovaConversation
				# The conversation itself, not just the flag. The task modal loads through
				# ForecastQueryRenderer, whose default store-and-network policy paints the cached store
				# before the network answers - so without replacing this list here, reopening a
				# discarded task rendered the retired conversation for a frame and then dropped it when
				# the fetch came back empty. Selecting it makes the discard write the now-empty list
				# straight into the store, so there is never a stale version to paint.
				novaThread {
					id
				}
			}
		}
	}
`;

function commit(
	environment: Environment,
	input: MutationInput<DiscardNovaConversationInput>,
	onSuccess?: RelayOnCompleted<DiscardNovaConversationMutation>,
	onError?: RelayOnError
): Disposable {
	const variables = {input: {csrfToken: Util.getCsrfValue(), socketClientId: Util.getClientId(), ...input}};
	return commitMutation<DiscardNovaConversationMutation>(environment, {
		mutation,
		variables,
		onCompleted: onSuccess,
		onError: onError,
	});
}

export default {commit};
